import { useEffect, useRef, useState } from 'react';

const TRIGGER_DISTANCE = 82;
const MAX_DISTANCE = 118;

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(
    target.closest('input, textarea, select, button, video, audio, [contenteditable="true"], [data-disable-pull-refresh]'),
  );
}

export default function PullToRefresh() {
  const startYRef = useRef<number | null>(null);
  const trackingRef = useRef(false);
  const distanceRef = useRef(0);
  const refreshingRef = useRef(false);
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const updateDistance = (value: number) => {
      distanceRef.current = value;
      setDistance(value);
    };

    const reset = () => {
      startYRef.current = null;
      trackingRef.current = false;
      if (!refreshingRef.current) updateDistance(0);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (
        refreshingRef.current
        || event.touches.length !== 1
        || window.scrollY > 0
        || isInteractiveTarget(event.target)
      ) {
        reset();
        return;
      }
      startYRef.current = event.touches[0].clientY;
      trackingRef.current = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!trackingRef.current || startYRef.current === null || event.touches.length !== 1) return;
      const rawDistance = event.touches[0].clientY - startYRef.current;
      if (rawDistance <= 0 || window.scrollY > 0) {
        reset();
        return;
      }
      updateDistance(Math.min(MAX_DISTANCE, rawDistance * 0.52));
      if (rawDistance > 8 && event.cancelable) event.preventDefault();
    };

    const onTouchEnd = () => {
      if (!trackingRef.current) return;
      trackingRef.current = false;
      startYRef.current = null;
      if (distanceRef.current >= TRIGGER_DISTANCE) {
        refreshingRef.current = true;
        setRefreshing(true);
        updateDistance(TRIGGER_DISTANCE);
        window.setTimeout(() => window.location.reload(), 180);
      } else {
        updateDistance(0);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', reset, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', reset);
    };
  }, []);

  if (distance <= 0 && !refreshing) return null;
  const ready = distance >= TRIGGER_DISTANCE;

  return (
    <div
      className="pointer-events-none fixed left-1/2 top-3 z-[100] transition-transform duration-150"
      style={{ transform: `translate(-50%, ${Math.max(0, distance - 42)}px)` }}
      aria-live="polite"
    >
      <div className="flex items-center gap-2 rounded-full bg-foreground-950/90 px-4 py-2 text-xs font-medium text-white shadow-lg backdrop-blur-sm">
        <i className={`${refreshing ? 'ri-loader-4-line animate-spin' : ready ? 'ri-refresh-line' : 'ri-arrow-down-line'} text-base`} />
        {refreshing ? '새로고침 중...' : ready ? '놓으면 새로고침' : '아래로 당겨 새로고침'}
      </div>
    </div>
  );
}
